import React, { useState, useEffect, useRef } from "react";
import {
  CompareMatrixSectionType,
  CompareMatrixSectionFeatureType,
} from "../types/CompareMatrixPricing";
import Tooltip from "../Tooltip/Tooltip";
import * as Collapsible from "@radix-ui/react-collapsible";
import { AnimatePresence, motion } from "framer-motion";
import Check from "../SVGs/Check";
import Chevron from "../SVGs/Chevron";

const CompareMatrixSectionMobile: React.FC<CompareMatrixSectionType> = ({
  sectionTitle,
  sectionIcon,
  planValue,
  features,
}) => {
  const [open, setOpen] = useState(true);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (headerRef.current) {
      const rect = headerRef?.current.getBoundingClientRect();

      if (rect.top < 0 && !open) {
        headerRef.current?.scrollIntoView({
          behavior: "instant",
          block: "start",
        });
      }
    }
  }, [open]);

  /* The mobile version deviates from the Builder/Desktop data structure
   * due to the design, so let's create a sane shape.
   */
  const createSection = (
    features: CompareMatrixSectionFeatureType,
    row: number
  ) => {
    const rowNumber = `row${row}`;
    return features.map((feature: CompareMatrixSectionFeatureType) => ({
      name: feature.name,
      tooltip: feature.tooltip,
      addNewBadge: feature.addNewBadge,
      row: feature[rowNumber],
    }));
  };
  const starter = createSection(features, 1);
  const essential = createSection(features, 2);
  const plus = createSection(features, 3);

  const determineSelectedPlan = (planValue: string | undefined) => {
    switch (planValue) {
      case "essential":
        return essential;
      case "plus":
        return plus;
      default:
        return starter;
    }
  };

  const currentPlan = determineSelectedPlan(planValue);

  return (
    <div
      ref={headerRef}
      className="compare-section compare-matrix-pricing__font-set scroll-mt-[56px] group transition duration-200 delay-75"
    >
      <Collapsible.Root
        className="CollapsibleRoot"
        open={open}
        onOpenChange={setOpen}
      >
        <div className="flex justify-between sticky top-[56px] z-20">
          <Collapsible.Trigger asChild>
            <button className="compare-section__header flex relative w-full mb-xxxs pt-xs pb-xs bg-white after:content-[''] after:h-px after:w-screen after:absolute after:bottom-0 after:-left-5 after:bg-forest-400">
              <div className="compare-section__btn flex items-center w-full">
                <img
                  className="w-6 h-6 self-center mr-xxxxs"
                  src={sectionIcon.icon}
                  alt={sectionIcon.iconAltText}
                />
                <h3 className="compare-matrix-pricing__font-set body--text-extra-large !leading-[0] pr-xxxxs">
                  {sectionTitle}
                </h3>
                <span className="compare-matrix-pricing__font-set sr-only">{`${
                  open ? "Collapse" : "Expand"
                }`}</span>
                <Chevron
                  className={`chevron w-4 h-3 ml-auto transition-transform motion-reduce:transition-none ${
                    open ? "chevon--active rotate-180" : "rotate-0"
                  }`}
                  aria-hidden="true"
                />
              </div>
            </button>
          </Collapsible.Trigger>
        </div>
        <Collapsible.Content forceMount>
          <AnimatePresence initial={true}>
            {open && (
              <motion.div
                initial={{ opacity: 0, height: 0, translateY: 15 }}
                animate={{ opacity: 1, height: "100%", translateY: 0 }}
                exit={{ opacity: 0, height: 0, translateY: 15 }}
                transition={{ duration: 0.2 }}
              >
                {currentPlan.map(
                  (item: CompareMatrixSectionFeatureType, key: number) => {
                    const { name, tooltip, addNewBadge, row } = item;
                    return (
                      <div
                        className="compare-row-mobile__item flex items-center justify-between border-b pt-xxxxs pb-xs mb-xxxxs min-h-8 last:mb-0 last:border-b-0"
                        key={key}
                      >
                        <div className="compare-row-mobile__info inline basis-2/3">
                          <span
                            className={`compare-row-mobile__heading compare-matrix-pricing__font-set ${
                              tooltip ? "mr-1.5" : "mr-2"
                            } `}
                          >
                            {name}
                          </span>
                          <div className="inline-block">
                            {tooltip && (
                              <span className="compare-row-mobile__tooltip inline-block whitespace-nowrap mr-xxxxs relative -left-1.5">
                                <Tooltip copy={tooltip} />
                              </span>
                            )}
                            {addNewBadge && (
                              <span
                                className={`compare-row-mobile__badge compare-matrix-pricing__font-set body--text-eyebrow pt-[5px] pb-1 px-1.5 bg-sky-500 rounded-xxs inline-block whitespace-nowrap relative ${
                                  tooltip ? "-left-3" : ""
                                }`}
                              >
                                New
                              </span>
                            )}
                          </div>
                        </div>
                        <div
                          className="compare-row-mobile__cell basis-1/3"
                          key={key}
                        >
                          <div className="compare-row-mobile__cell-item flex justify-end">
                            {row.cellItem === "Check" ? (
                              <span className="compare-row__check items-end block w-6 h-6 relative left-1">
                                <Check size="small" />
                              </span>
                            ) : row.cellItem === "Add on" ? (
                              <span className="compare-row__add-on compare-matrix-pricing__font-set flex items-center justify-center pl-4">
                                {row.addOnText}
                              </span>
                            ) : (
                              <span></span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </Collapsible.Content>
      </Collapsible.Root>
    </div>
  );
};

export default CompareMatrixSectionMobile;
